import type { CaptureResult, PostHogConfig } from "posthog-js"

// Keep only structural selectors. Masking alone still permits link destinations
// and data-ph-capture-* augmentations in the SDK's autocapture payload.
export function privateElementsChain(chain: string): string | null {
  const elements = []
  const pattern = /(?:^|;)([a-z][a-z0-9]*):((?:[\w-]+="(?:\\.|[^"\\])*")*)(?=;|$)/g
  let position = 0
  for (const match of chain.matchAll(pattern)) {
    if (match.index !== position) return null
    position = match.index + match[0].length
    const attributes = new Map(
      Array.from(match[2].matchAll(/([\w-]+)="((?:\\.|[^"\\])*)"/g), (attribute) => [attribute[1], attribute[2]])
    )
    const child = attributes.get("nth-child") ?? "0"
    const type = attributes.get("nth-of-type") ?? "0"
    if (!/^\d{1,6}$/.test(child) || !/^\d{1,6}$/.test(type)) return null
    elements.push(`${match[1]}:nth-child="${child}"nth-of-type="${type}"`)
  }
  return position === chain.length && elements.length > 0 ? elements.join(";") : null
}

// This is an allowlist, not a best-effort SSN/account-number regex. No page
// text, field values, attributes, or titles are forwarded. User identity comes
// only from the signed-in user, never captured page/person properties.
export type PosthogUserIdentity = { distinctId: string; email: string; username: string }

export function synchronizePosthogIdentity(
  client: {
    get_distinct_id(): string
    reset(): void
    identify(id: string, properties: { email: string; username: string }): void
  },
  user: PosthogUserIdentity | undefined,
  setIdentity: (user: PosthogUserIdentity | undefined) => void
) {
  // Close the gate before reset/identify can emit events. Check the SDK's
  // persisted identity, not merely the identity observed during this mount.
  setIdentity(undefined)
  if (!user || client.get_distinct_id() !== user.distinctId) client.reset()
  if (!user) return
  setIdentity(user)
  client.identify(user.distinctId, { email: user.email, username: user.username })
}

export function filterPosthogEvent(event: CaptureResult | null, user?: PosthogUserIdentity): CaptureResult | null {
  if (!event || !["$autocapture", "$pageview", "$identify", "$set"].includes(event.event)) return null
  // Restored SDK identity is not proof of the current Clerk identity. This
  // also rejects events generated under a previously authenticated account.
  if (!user || event.properties.distinct_id !== user.distinctId) return null
  const input = event.properties
  const properties: Record<string, string | boolean | { email: string; username: string }> = {}
  const identityPattern = /^(?:user_[a-zA-Z0-9]+|[0-9a-f-]{32,36})$/
  for (const key of ["distinct_id", "$device_id", "$session_id", "$window_id", "$anon_distinct_id"]) {
    if (typeof input[key] === "string" && identityPattern.test(input[key])) properties[key] = input[key]
  }
  if (!properties.distinct_id) return null
  for (const key of ["$is_identified", "$process_person_profile"]) {
    if (typeof input[key] === "boolean") properties[key] = input[key]
  }
  for (const key of ["$current_url", "$referrer"]) {
    if (typeof input[key] !== "string") continue
    try {
      const url = new URL(input[key])
      if (!["https:", "http:"].includes(url.protocol) || url.username || url.password) continue
      const decoded = decodeURIComponent(url.toString())
      if (decoded.includes("@") || /\b\d{3}[- ]\d{2}[- ]\d{4}\b/.test(decoded)) continue
      if (
        Array.from(url.searchParams.keys()).some((name) =>
          /^(?:ssn|social_security_number|account_?number|email)$/i.test(name)
        )
      )
        continue
      properties[key] = url.toString()
    } catch {
      // Non-URL referrer values are not useful for these product questions.
    }
  }
  properties.$lib = "web"
  properties.$geoip_disable = true
  if (event.event === "$autocapture") {
    if (input.$event_type !== "click" || typeof input.$elements_chain !== "string") return null
    const chain = privateElementsChain(input.$elements_chain)
    if (!chain) return null
    properties.$event_type = "click"
    properties.$elements_chain = chain
  }
  const clean: CaptureResult = { uuid: event.uuid, event: event.event, timestamp: event.timestamp, properties }
  if (event.event === "$identify" || event.event === "$set") {
    if (!user || properties.distinct_id !== user.distinctId) return null
    const identity = { email: user.email, username: user.username }
    if (event.event === "$identify") clean.$set = identity
    else properties.$set = identity
  }
  return clean
}

export const posthogPrivacyConfig = {
  autocapture: { dom_event_allowlist: ["click"], capture_copied_text: false },
  mask_all_text: true,
  mask_all_element_attributes: true,
  capture_pageview: false,
  capture_pageleave: false,
  capture_heatmaps: false,
  capture_performance: false,
  capture_dead_clicks: false,
  capture_exceptions: false,
  rageclick: false,
  disable_session_recording: true,
  enable_recording_console_log: false,
  disable_surveys: true,
  disable_external_dependency_loading: true,
  advanced_disable_flags: true,
  before_send: filterPosthogEvent,
} satisfies Partial<PostHogConfig>
